import { deriveCatchupParams } from '@/features/advanced-twap/lib/derive-catchup-params'
import { parseTotalSlices, percentToFraction } from '@/features/advanced-twap/lib/wire-format'
import { safeParseFloat } from '@/lib/utils'
import { AdvancedTwapAlgoParams } from '@/types/generated/algo'
import { Denomination, OrderSide } from '@/types/generated/common/trading'
import { Exchange, TradeVenue } from '@/types/generated/common/types'

export type AdvancedTwapRawSettings = {
  durationSeconds: number
  totalSlices: string
  bandEnabled: boolean
  bandPct: number
  reduceOnly: boolean
}

/**
 * Builds AdvancedTwapAlgoParams from one resolved order row + the form's raw settings.
 * Single source of truth for the manual Advanced TWAP submit. Throws user-facing
 * messages on invalid input, reusing the contract's own wording (§11) where it says to
 * surface refusals verbatim.
 *
 * exchange/tradeVenue are left at their proto zero value (UNSPECIFIED) rather than
 * client-populated — routing identity is server-attached at the public-api boundary
 * (matches plain TWAP's actual submit path, which never passes them either).
 *
 * catchup has no user-facing inputs — per the #trading-desk Slack thread (2026-09-17),
 * the frontend derives frequency + order_size from duration/total_slices/quantity alone
 * (see derive-catchup-params.ts) so it can never undercut what a slice needs to fill
 * within its own lifetime, the bug this replaced ("slice is Ns but its catchup leg needs
 * Ms"). qtyStep/minOrderSize are the instrument's own increment/floor, used only to round
 * the derived catchup clip size to a valid order.
 */
export function buildAdvancedTwapAlgoParams(args: {
  symbol: string
  quantity: string
  side: 'buy' | 'sell'
  accountId: string
  denomination: Denomination
  qtyStep?: number
  minOrderSize?: number
  raw: AdvancedTwapRawSettings
}): AdvancedTwapAlgoParams {
  const { symbol, quantity, side, accountId, denomination, qtyStep, minOrderSize, raw } = args

  if (!(raw.durationSeconds > 0)) {
    throw new Error('duration_seconds must be greater than zero')
  }

  const totalSlices = parseTotalSlices(raw.totalSlices)
  if (totalSlices === null) {
    throw new Error('Total slices must be a positive whole number')
  }

  if (raw.bandEnabled && !(raw.bandPct > 0)) {
    throw new Error('Please enter a band percentage')
  }

  const catchup = deriveCatchupParams({
    durationSeconds: raw.durationSeconds,
    requestedTotalSlices: totalSlices || 10,
    quantity: safeParseFloat(quantity),
    qtyStep,
    minOrderSize,
  })
  if (catchup === null) {
    throw new Error('Could not size the catchup leg for this order — try a longer duration')
  }

  const bandFraction = raw.bandEnabled ? percentToFraction(String(raw.bandPct)) : undefined

  return {
    symbol,
    quantity,
    orderSide: side === 'buy' ? OrderSide.ORDER_SIDE_BUY : OrderSide.ORDER_SIDE_SELL,
    accountId,
    exchange: Exchange.EXCHANGE_UNSPECIFIED,
    denomination,
    tradeVenue: TradeVenue.TRADE_VENUE_UNSPECIFIED,
    durationSeconds: raw.durationSeconds.toString(),
    totalSlices,
    upperBoundPct: bandFraction,
    lowerBoundPct: bandFraction,
    catchup: {
      frequency: { fixed: catchup.frequencyMs.toString() },
      orderSize: { fixed: catchup.orderSize },
    },
    reduceOnly: raw.reduceOnly || undefined,
  }
}
